import ArrowDownIcon from '@ben/icons/arrow-down-s-line';
import Menu from '@ben/orion/menu';
import Icon from '@ben/orion/remix-icon';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { CustomComponentTypes } from '@ben/shared/constants';
import { Divider } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';
import RestrictionGroup from './restriction-group';
import { type RestrictionType } from './types';
import {
  RESTRICTION_CONFIG,
  useRestrictionFieldArrays,
} from './use-restriction-field-arrays';

const RESTRICTION_SET_OPTIONS: {
  label: string;
  value: RestrictionType;
}[] = [
  {
    label: 'Frequency limits',
    value: 'frequencyLimits',
  },
  {
    label: 'Step size limits',
    value: 'stepSizeLimits',
  },
  {
    label: 'Total limits',
    value: 'totalLimits',
  },
  {
    label: 'Life event limits',
    value: 'lifeEventLimits',
  },
  {
    label: 'Formula based restrictions',
    value: 'formulaBasedRestrictions',
  },
  {
    label: 'Exclusion periods',
    value: 'exclusionPeriods',
  },
  {
    label: 'Conditional contact age restrictions',
    value: 'conditionalContactAgeRestrictions',
  },
];

const COMPONENTS_WITH_FULL_RESTRICTIONS = new Set([
  CustomComponentTypes.DROPDOWN,
  CustomComponentTypes.AMOUNT_INPUT,
  CustomComponentTypes.PENSION_SLIDERS,
  CustomComponentTypes.SLIDER,
  CustomComponentTypes.BUY_SELL_HOLIDAY,
]);

const isRestrictionSupported = (
  componentType: CustomComponentTypes,
  restrictionType: RestrictionType
): boolean => {
  if (restrictionType === 'conditionalContactAgeRestrictions') {
    return componentType === CustomComponentTypes.CONTACTS;
  }

  if (
    restrictionType === 'formulaBasedRestrictions' ||
    restrictionType === 'exclusionPeriods'
  ) {
    return true;
  }

  return COMPONENTS_WITH_FULL_RESTRICTIONS.has(componentType);
};

interface RestrictionSetProps {
  componentType: CustomComponentTypes;
  componentControllerName: string;
}

const RestrictionSet = ({
  componentType,
  componentControllerName,
}: RestrictionSetProps) => {
  const [opened, { toggle, close }] = useDisclosure(false);
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);

  const { control, setValue, trigger } = useFormContext();

  const restrictionSetPath = `${componentControllerName}.restrictionSet`;

  const fieldArrays = useRestrictionFieldArrays(control, restrictionSetPath);

  const availableOptions = useMemo(() => {
    return RESTRICTION_SET_OPTIONS.filter((option) =>
      isRestrictionSupported(componentType, option.value)
    );
  }, [componentType]);

  const handleRestrictionSelect = useCallback(
    (restrictionType: RestrictionType) => {
      const config = RESTRICTION_CONFIG[restrictionType];
      fieldArrays[restrictionType].append(config.defaultValues);

      if (!openAccordions.includes(restrictionType)) {
        setOpenAccordions((previous) => [...previous, restrictionType]);
      }

      close();
    },
    [close, fieldArrays, openAccordions]
  );

  const handleAccordionToggle = useCallback(
    (accordionValue: string) => (isOpen: boolean) => {
      setOpenAccordions((previous) => {
        if (isOpen) {
          return previous.includes(accordionValue)
            ? previous
            : [...previous, accordionValue];
        }
        return previous.filter((value) => value !== accordionValue);
      });
    },
    []
  );

  const hasAnyRestrictions = useMemo(() => {
    return Object.values(fieldArrays).some(
      (restrictionArray) => restrictionArray.fields.length > 0
    );
  }, [fieldArrays]);

  if (availableOptions.length === 0) {
    return null;
  }

  return (
    <Container>
      {hasAnyRestrictions && (
        <>
          <StyledDivider />
          <TitleWrapper>
            <Typography variant={TypographyVariant.bodyMd2}>
              Restriction sets
            </Typography>
          </TitleWrapper>
        </>
      )}

      {availableOptions.map((option) => {
        const type = option.value;
        const { fields, remove } = fieldArrays[type];

        if (fields.length === 0) {
          return null;
        }

        const {
          component: Component,
          title,
          needsSetValue,
          needsTrigger,
        } = RESTRICTION_CONFIG[type];

        return (
          <RestrictionGroup
            key={type}
            title={title}
            isOpen={openAccordions.includes(type)}
            onToggle={handleAccordionToggle(type)}
          >
            {fields.map((field, index) => (
              <Component
                key={field.id}
                index={index}
                control={control}
                restrictionSetPath={restrictionSetPath}
                onRemove={() => remove(index)}
                {...(needsSetValue ? { setValue } : {})}
                {...(needsTrigger ? { trigger } : {})}
              />
            ))}
          </RestrictionGroup>
        );
      })}

      <MenuWrapper>
        <Menu
          position="bottom-start"
          opened={opened}
          onChange={toggle}
          closeOnItemClick={false}
          width={335}
        >
          <Menu.Target>
            <SelectButton type="button" $isOpen={opened}>
              <Typography variant={TypographyVariant.bodyMd1}>
                Add restriction
              </Typography>
              <Icon
                icon={ArrowDownIcon}
                size={18}
                style={{
                  transform: opened ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 150ms ease',
                }}
              />
            </SelectButton>
          </Menu.Target>
          <Menu.Dropdown w={335}>
            {availableOptions.map((option) => (
              <Menu.Item
                key={option.value}
                onClick={() => handleRestrictionSelect(option.value)}
              >
                {option.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </MenuWrapper>
    </Container>
  );
};

export default RestrictionSet;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    width: 100%;
  `}
`;

const SelectButton = styled.button<{ $isOpen: boolean }>`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 335px;
    height: 40px;
    padding: ${theme.spacing(1, 1.5)};
    border: 1px solid ${theme.palette.neutral[100]};
    border-radius: ${theme.borderRadius.sm};
    background-color: ${theme.palette.neutral['00']};
    cursor: pointer;
    transition: border-color 150ms ease;

    &:hover {
      border-color: ${theme.palette.primary.main};
    }

    &:focus {
      outline: 2px solid ${theme.palette.primary.main};
      outline-offset: 2px;
    }
  `}
`;

const MenuWrapper = styled.div`
  position: relative;
`;

const TitleWrapper = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(1)};
  `}
`;

const StyledDivider = styled(Divider)`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(2)};
  `}
`;
